import produce from 'immer';
import {
  ReadSearchApiArg,
  SearchFilterConditionDate,
  SearchScope,
} from 'lib-search-types';
import { isDateFilterCondition, isLeafFilterCondition } from 'lib-search-ui';
import type { ConversationStatus } from 'lib-shared-types-api';
import moment from 'moment-timezone';
import { Writable } from 'ts-essentials';

export function transformReadSearchArg<
  TScope extends SearchScope,
  TEnriched extends boolean = false,
>(arg: ReadSearchApiArg<TScope, TEnriched>): typeof arg {
  return produce(arg, (draftArg) => {
    // Quoted queries (e.g. "John Doe") enable strict/exact matching
    if (draftArg.query) {
      const normalized = draftArg.query.replace(
        /[\u201C\u201D\u201E\uFF02]/g,
        '"',
      );

      if (
        normalized.length > 2 &&
        normalized.startsWith('"') &&
        normalized.endsWith('"')
      ) {
        const inner = normalized.slice(1, -1);

        if (!inner.includes('"')) {
          draftArg.query = inner;
          draftArg.isStrict = true;
        }
      }
    }

    if (draftArg.filters) {
      draftArg.filters.filters.forEach((condition) => {
        if (
          isLeafFilterCondition(condition) &&
          isDateFilterCondition(condition)
        ) {
          (condition as Writable<SearchFilterConditionDate>).meta = {
            timezone: condition.meta?.timezone ?? moment.tz.guess(),
          };
        }
      });
    }
  });
}

export function transformReadAdvancedSearchResultsArg(
  arg: ReadSearchApiArg<'conversations', true>,
): ReadSearchApiArg<'conversations', true> {
  const base = transformReadSearchArg(
    arg as unknown as ReadSearchApiArg,
  ) as unknown as ReadSearchApiArg<'conversations', true>;

  return produce(base, (draft) => {
    draft.filters?.filters.forEach((condition) => {
      if (!isLeafFilterCondition(condition)) return;

      // Enriches a conversations search request so that status filters include implied statuses
      // (e.g. open/pending → awaitingpending, closed → pending).
      if (condition.field === 'status') {
        const statuses = new Set<ConversationStatus>(
          condition.values.map(({ value }) => value as ConversationStatus),
        );

        if (statuses.has('open') || statuses.has('pending')) {
          statuses.add('awaitingpending');
        }
        if (statuses.has('closed')) {
          statuses.add('pending');
        }

        (condition as Writable<typeof condition>).values = [...statuses].map(
          (value) => ({ value }),
        );
      }
    });
  });
}
